import { useMemo, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/config/api';
import { ShieldCheck, UploadCloud, FileSearch, XCircle, CheckCircle2 } from 'lucide-react';

function PrettyJson({ value }) {
  const text = useMemo(() => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value ?? '');
    }
  }, [value]);

  return (
    <pre className="text-xs whitespace-pre-wrap break-words bg-slate-950 text-slate-100 rounded-lg p-3 overflow-auto max-h-[420px]">
      {text}
    </pre>
  );
}

export default function VerifyWatermark({ token, user }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onSelectFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setResult(null);
  };

  const onVerify = async () => {
    if (!file) {
      toast.error('Please select a PDF or image file first.');
      return;
    }
    try {
      setLoading(true);
      setResult(null);

      const form = new FormData();
      form.append('file', file);

      const resp = await axios.post(`${API}/watermark/verify`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          // Let axios set multipart boundary automatically
        }
      });

      setResult(resp.data);
      toast.success('Watermark verification completed.');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to verify watermark';
      toast.error(msg);
      setResult({ error: msg, raw: err?.response?.data });
    } finally {
      setLoading(false);
    }
  };

  const signatureValid = result?.signature_valid;
  const matchedHop = result?.matched_hop;
  const chain = result?.chain || [];
  const matchedHandoffSeq = matchedHop?.handoff_seq ?? matchedHop?.hop_id;

  return (
    <DashboardLayout user={user}>
      <div className="container mx-auto px-4 py-4 sm:py-6 lg:py-8 w-full min-w-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4 sm:mb-6">
          <div
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' }}
          >
            <ShieldCheck className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Verify Watermark</h1>
            <p className="text-xs sm:text-sm text-gray-600">
              Upload a leaked PDF/image to verify the forensic watermark and attribute the hop.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <Card className="border border-gray-200 min-w-0">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <UploadCloud className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                Upload file
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Supported: PDF and images. For images, verify the PNG saved from the viewer.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Leaked file</label>
                <Input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={onSelectFile}
                />
                {file && (
                  <div className="text-xs text-gray-600">
                    Selected: <span className="font-semibold">{file.name}</span> ({Math.round(file.size / 1024)} KB)
                  </div>
                )}
              </div>

              <Button
                onClick={onVerify}
                disabled={loading || !file}
                className="w-full"
                style={{ background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' }}
              >
                <FileSearch className="w-4 h-4 mr-2" />
                {loading ? 'Verifying…' : 'Verify Watermark'}
              </Button>

              <div className="text-xs text-gray-500">
                Note: Only the <span className="font-semibold">document owner</span> can verify and attribute watermarks.
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200 min-w-0">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex flex-wrap items-center gap-2 text-base sm:text-lg">
                Results
                {result && typeof signatureValid === 'boolean' && (
                  <Badge variant={signatureValid ? 'secondary' : 'destructive'} className="text-xs">
                    {signatureValid ? 'Signature valid' : 'Signature invalid'}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Shows extracted payload, signature validation, and matched hop (if found).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
              {!result ? (
                <div className="text-sm text-gray-600">
                  No result yet. Upload a file and click verify.
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {typeof signatureValid === 'boolean' && (
                      <Badge
                        variant={signatureValid ? 'secondary' : 'destructive'}
                        className="flex items-center gap-1"
                      >
                        {signatureValid ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {signatureValid ? 'Valid signature' : 'Invalid signature'}
                      </Badge>
                    )}
                    {result?.env_id && (
                      <Badge variant="outline">env_id: {result.env_id}</Badge>
                    )}
                    {result?.payload?.udt_id && (
                      <Badge variant="outline">udt_id: {result.payload.udt_id}</Badge>
                    )}
                    {typeof result?.chain_length === 'number' && (
                      <Badge variant="outline">chain: {result.chain_length}</Badge>
                    )}
                  </div>

                  {matchedHop ? (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                      <div className="text-sm font-semibold text-green-800 mb-1">Leak attributed to (matched hop)</div>
                      <div className="text-xs text-green-800">
                        Shared from <span className="font-semibold">{matchedHop.sender_name || matchedHop.sender || '—'}</span>
                        {matchedHop.sender_udt_id && <span className="font-mono ml-1">({matchedHop.sender_udt_id})</span>}
                        {' to '}
                        <span className="font-semibold">{matchedHop.receiver_name || matchedHop.requester_name || matchedHop.recipient_name || matchedHop.receiver || '—'}</span>
                        {matchedHop.receiver_udt_id && <span className="font-mono ml-1">({matchedHop.receiver_udt_id})</span>}
                        {' '}(hop #{String(matchedHop.handoff_seq ?? matchedHop.hop_id ?? '')})
                      </div>
                      <p className="text-xs text-green-700 mt-1">Same UDT User ID appears in Evidence Vault and everywhere for this person.</p>
                    </div>
                  ) : result?.error ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <div className="text-sm font-semibold text-red-800 mb-1">Error</div>
                      <div className="text-xs text-red-800">{String(result.error)}</div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <div className="text-sm font-semibold text-amber-800 mb-1">No hop match</div>
                      <div className="text-xs text-amber-800">
                        Watermark was extracted, but it didn’t match any stored hop. This can happen if the file wasn’t
                        downloaded from the viewer or if the chain record was cleared.
                      </div>
                    </div>
                  )}

                  {chain.length > 0 && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4 min-w-0">
                      <div className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">
                        All recipients (document lifecycle)
                      </div>
                      <p className="text-xs text-gray-600 mb-3 break-words">
                        Everyone who received this document. UDT User ID in parentheses is the same per person in Evidence Vault and here. The leak is attributed to the matched hop above.
                      </p>
                      <ul className="space-y-2 max-h-48 overflow-y-auto min-w-0">
                        {chain.map((hop, index) => {
                          const isOrigin = index === 0 && (hop.sender === 'OWNER' || !hop.sender);
                          const receiverName = hop.receiver_name || hop.requester_name || hop.recipient_name || hop.receiver || '—';
                          const senderName = hop.sender_name || (hop.sender === 'OWNER' ? 'Document Owner' : hop.sender || '—');
                          const receiverUdtId = hop.receiver_udt_id || '';
                          const senderUdtId = hop.sender_udt_id || '';
                          const hopSeq = hop.handoff_seq ?? hop.hop_id ?? index;
                          const isMatched = matchedHop && (
                            String(hop.handoff_seq) === String(matchedHandoffSeq) ||
                            (hop.receiver === matchedHop.receiver && String(hop.handoff_seq) === String(matchedHop.handoff_seq))
                          );
                          const highlight = !!isMatched;
                          return (
                            <li
                              key={hop.hop_id ?? index}
                              className={`text-xs py-2 px-3 rounded border break-words ${highlight ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}
                            >
                              {isOrigin ? (
                                <span className="text-gray-700">Document uploaded (owner{senderUdtId ? ` · ${senderUdtId}` : ''})</span>
                              ) : (
                                <>
                                  <span className="text-gray-700">
                                    #{hopSeq} — From <span className="font-medium">{senderName}</span>{senderUdtId && <span className="font-mono text-gray-600"> ({senderUdtId})</span>} to <span className="font-medium">{receiverName}</span>{receiverUdtId && <span className="font-mono text-gray-600"> ({receiverUdtId})</span>}
                                  </span>
                                  {highlight && (
                                    <Badge className="ml-2 bg-green-600 text-xs">Leak source</Badge>
                                  )}
                                </>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-2">Full response</div>
                    <PrettyJson value={result} />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}


